import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  sidebarContainerSx,
  sidebarIconsContainerSx,
  sidebarIconButtonSx,
  activeIconSx,
  expandedTextSx,
  toggleIconColor,
  defaultIconColor,
  submenuArrowSx,
} from './SidebarStyles';

import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import TrainIcon from '@mui/icons-material/Train';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import DirectionsRailwayIcon from '@mui/icons-material/DirectionsRailway';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import OutboxIcon from '@mui/icons-material/Outbox';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SchemaIcon from '@mui/icons-material/Schema';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import AnalyticsIcon from '@mui/icons-material/Analytics'; 
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BuildIcon from '@mui/icons-material/Build';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BlockIcon from '@mui/icons-material/Block';

const Sidebar = ({ isExpanded, setIsExpanded }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);

  const handleMenuClick = (event, menu) => {
    if (activeMenu === menu) {
      setAnchorEl(null);
      setActiveMenu(null);
    } else {
      setAnchorEl(event.currentTarget);
      setActiveMenu(menu);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const navItems = [
    { label: 'Home', icon: <HomeIcon />, path: '/home', exact: true },
    { label: 'Operator', icon: <PersonIcon />, path: '/home/operator' },
    {
      label: 'Configurations',
      icon: <SettingsIcon />,
      children: [
        { label: 'Units', icon: <TrainIcon />, path: '/home/units' },
        { label: 'Evns', icon: <DirectionsRailwayIcon />, path: '/home/evns' },
        { label: 'Meters', icon: <FlashOnIcon />, path: '/home/meters' },
      ],
    },
    {
      label: 'Logs',
      icon: <ListAltIcon />,
      children: [
        { label: 'Incoming', icon: <MoveToInboxIcon />, path: '/home/incoming-logs' },
        { label: 'Export', icon: <OutboxIcon />, path: '/home/export-logs' },
        { label: 'Structure', icon: <SchemaIcon />, path: '/home/structure-logs' },
      ],
    },
    {
      label: 'Reports',
      icon: <BarChartIcon />,
      children: [
        { label: 'Data Quality', icon: <AnalyticsIcon />, path: '/home/data-quality' }, 
        { label: 'Operator Export Summary', icon: <AnalyticsIcon />, path: '/home/operator-export-summary' },
        { label: 'Excluded Units', icon: <BlockIcon />, path: '/home/export-unit-exclusions' }
      ],
    },
    {
      label: 'Modifications',
      icon: <BuildIcon />,
      children: [
        { label: 'Create Configuration', icon: <AddCircleOutlineIcon />, path: '/home/create-config' },
        { label: 'Meter Changes', icon: <SwapHorizIcon />, path: '/home/meter-changes' },
      ],
    },
  ];

  return (
    <Box sx={{ ...sidebarContainerSx, width: isExpanded ? '240px' : '95px' }}>
      <Box sx={sidebarIconsContainerSx(isExpanded)}>
        <IconButton
          onClick={() => setIsExpanded((prev) => !prev)}
          sx={{
            ...sidebarIconButtonSx,
            ...toggleIconColor(isExpanded),
            mb: 2.5,
            alignSelf: isExpanded ? 'flex-end' : 'center',
          }}
        >
          {isExpanded ? <MenuOpenIcon /> : <MenuIcon />}
        </IconButton>

        {navItems.map((item) => {
          const hasChildren = !!item.children;
          const isItemActive =
            (item.path && isActive(item.path, item.exact)) ||
            item.children?.some((child) => isActive(child.path));

          return (
            <Box
              key={item.label}
              onClick={(e) =>
                hasChildren ? handleMenuClick(e, item.label) : navigate(item.path)
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                px: isExpanded ? 1 : 0,
                py: 0.5,
                borderRadius: '0.5rem',
                justifyContent: isExpanded ? 'flex-start' : 'center',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <Tooltip title={!isExpanded ? item.label : ''} placement="right">
                <IconButton disableRipple sx={sidebarIconButtonSx}>
                  {React.cloneElement(item.icon, {
                    fontSize: 'small',
                    sx: isItemActive ? activeIconSx : defaultIconColor,
                  })}
                </IconButton>
              </Tooltip>

              {isExpanded && (
                <>
                  <Typography sx={{ ...expandedTextSx, opacity: 1, transform: 'translateX(0)' }}>
                    {item.label}
                  </Typography>
                  {hasChildren && <ArrowRightIcon fontSize="small" sx={submenuArrowSx} />}
                </>
              )}
            </Box>
          );
        })}
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { mt: 1, minWidth: '180px' } }}
      >
        <List dense disablePadding>
          {navItems
            .find((item) => item.label === activeMenu)
            ?.children?.map((subItem) => (
              <ListItem
                sx={{ cursor: 'pointer' }}
                button
                key={subItem.label}
                onClick={() => {
                  navigate(subItem.path);
                  handleClose();
                }}
              >
              <ListItemIcon sx={{ minWidth: 28, mr: 1 }}>  
                  {React.cloneElement(subItem.icon, {
                    fontSize: 'small',
                    sx: isActive(subItem.path) ? activeIconSx : defaultIconColor,
                  })}
                </ListItemIcon>
                <ListItemText primary={subItem.label} />
              </ListItem>
            ))}
        </List>
      </Popover>
    </Box>
  );
};

export default Sidebar;
